import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { useApp } from '@contexts/AppContext';
import { DEMO_USERS, authenticateLocal, roleKeyForEmail } from '@services/demoUsers';

/**
 * LoginPage — Two-tab login gate
 *
 * Tab 1: Local Login (demo accounts)
 *   - Quick-switch dropdown pre-fills name/email
 *   - Password with show/hide toggle
 *   - Employee: vandana.kabilan@sandvik.com / MrtAdmin@26
 *   - Customer: jm.beaulieu@brightstars.ca / MrtCust@26
 *   - Guest:    guest@example.com / MrtGuest@26
 *
 * Tab 2: Azure AD SSO (placeholder)
 *   - Will use MSAL redirect flow in production
 *
 * On login: sets AuthContext + dispatches sales area/entity/customer to AppContext
 * Redirects to / if already authenticated.
 *
 * Pattern: Bit Annotator login gate
 */
export default function LoginPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loginLocal } = useAuth();
  const { dispatch } = useApp();

  const [activeTab, setActiveTab] = useState('local');
  const [selectedUser, setSelectedUser] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  // Quick-switch: populate fields when a demo user is selected
  const handleQuickSwitch = (e) => {
    const val = e.target.value;
    setSelectedUser(val);
    setError('');
    if (val) {
      const user = DEMO_USERS.find(u => u.email === val);
      if (user) {
        setEmail(user.email);
        setPassword('');
      }
    } else {
      setEmail('');
      setPassword('');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    const result = authenticateLocal(email, password);
    if (!result.ok) {
      setError('Invalid email or password. Please try again.');
      return;
    }

    // Build the user object for AuthContext — use result.user (not result itself)
    const user = result.user;

    // Login via AuthContext (persists to localStorage)
    loginLocal(user, result.permissions || []);

    // Dispatch sales context to AppContext
    const acct = result.user;
    if (acct) {
      dispatch({ type: 'SET_SALES_AREA', payload: acct.salesArea || '' });
      dispatch({ type: 'SET_SALES_ENTITY', payload: acct.salesEntity || '' });
      dispatch({ type: 'SET_CUSTOMER_ACCOUNT', payload: acct.customerName || '' });
    }

    navigate('/', { replace: true });
  };

  const tabClass = (tab) =>
    `flex-1 py-3 text-sm font-medium text-center transition-colors ${
      activeTab === tab
        ? 'text-electric-blue border-b-2 border-electric-blue'
        : 'text-n5 border-b-2 border-transparent hover:text-carbon-black'
    }`;

  return (
    <div className="min-h-screen flex items-center justify-center bg-n1 px-4">
      <div className="w-full max-w-md">
        {/* Sandvik branding */}
        <div className="text-center mb-8">
          <img
            src="/logos/sandvik_logotype_black_rgb.svg"
            alt="Sandvik"
            className="h-8 mx-auto mb-3"
          />
          <h1 className="text-xl font-sandvik-headline font-bold text-carbon-black">
            My Rock Tools
          </h1>
          <p className="text-sm text-n5 mt-1">Sign in to your account</p>
        </div>

        {/* Login card */}
        <div className="bg-white border border-n2 shadow-sm">
          {/* Tabs */}
          <div className="flex border-b border-n2">
            <button className={tabClass('local')} onClick={() => setActiveTab('local')}>
              Local Login
            </button>
            <button className={tabClass('sso')} onClick={() => setActiveTab('sso')}>
              Azure AD SSO
            </button>
          </div>

          <div className="p-6">
            {activeTab === 'local' ? (
              <form onSubmit={handleLogin} className="space-y-4">
                {/* Quick-switch dropdown */}
                <div>
                  <label className="block text-xs text-n5 tracking-wide mb-1 font-sandvik-text">
                    Quick switch
                  </label>
                  <select
                    value={selectedUser}
                    onChange={handleQuickSwitch}
                    className="w-full border border-n3 rounded px-3 py-2 text-sm font-sandvik-text text-carbon-black
                               focus:ring-2 focus:ring-electric-blue focus:border-transparent"
                  >
                    <option value="">Select a demo account...</option>
                    {DEMO_USERS.map(u => (
                      <option key={u.email} value={u.email}>
                        {u.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="login-email" className="block text-sm font-medium text-n6 mb-1">
                    Email
                  </label>
                  <input
                    id="login-email"
                    type="email"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                    placeholder="you@company.com"
                    required
                    className="w-full border border-n3 rounded px-3 py-2 text-sm
                               focus:ring-2 focus:ring-electric-blue focus:border-transparent"
                  />
                </div>

                {/* Password with toggle */}
                <div>
                  <label htmlFor="login-password" className="block text-sm font-medium text-n6 mb-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => { setPassword(e.target.value); setError(''); }}
                      placeholder="Enter password"
                      required
                      className="w-full border border-n3 rounded px-3 py-2 pr-10 text-sm
                                 focus:ring-2 focus:ring-electric-blue focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-n4 hover:text-carbon-black"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <span className="material-symbols-outlined text-lg">
                        {showPassword ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <p className="text-sm text-error">{error}</p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  className="w-full py-2.5 bg-electric-blue text-white text-sm font-medium rounded
                             hover:bg-blue-dark transition-colors"
                >
                  Sign in
                </button>

                {/* Demo credentials hint */}
                <div className="mt-4 p-3 bg-n1 rounded text-xs text-n5 font-sandvik-text">
                  <p className="text-n6 mb-1">Demo credentials:</p>
                  <p>Employee: vandana.kabilan@sandvik.com / MrtAdmin@26</p>
                  <p>Customer: jm.beaulieu@brightstars.ca / MrtCust@26</p>
                  <p>Guest: guest@example.com / MrtGuest@26</p>
                </div>
              </form>
            ) : (
              <div className="text-center py-8">
                <span className="material-symbols-outlined text-4xl text-n3 mb-3 block">
                  lock
                </span>
                <p className="text-sm text-n5 mb-4 font-sandvik-text">
                  Azure AD Single Sign-On will be available when the MSAL integration is complete.
                </p>
                <button
                  disabled
                  className="px-6 py-2.5 bg-n2 text-n5 text-sm font-medium rounded cursor-not-allowed"
                >
                  Sign in with Azure AD
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
